/**
 * @description Add Script tag to the document body
 * @param url The location of the script to add
 * @param onload Callback to run when the script successfully loads
 */
export function appendScript(url: string, onload = () => {}) {
    if (url) {
        const script = document.createElement('script');
        script.src = url;
        script.onload = onload;

        document.body.appendChild(script);
    }
}

/**
 * @description Remove the oneTrust Manage Preferences link
 */
export function removeOneTrustLink() {
    const otManageLink = document.querySelector('[data-action="onetrust-manage"]');

    if (otManageLink) {
        otManageLink.remove();
    }
}

/**
 * @description Determine if the current page is using cannonbal V1 or V2 based upon HTML syntax
 * @returns True = is an CNBL V2 page; False = is a CNBL V1 page
 */
export function isV2Page(): boolean {
    return !document.documentElement.classList.contains('mlp');
}

/**
 * @description Determine if the current page is the OLP or a MLP landing page
 * @return True = OLP; False = MLP
 */
export function isOlp(): boolean {
    return !!(document.body.getAttribute('data-olp-page') === 'true');
}

/**
 * @description Determine if the current page is a Splash landing page
 * @return True = Splash; False = OLP/MLP
 */
export function isV2Splash(): boolean {
    if (isV2Page() && !isOlp()) {
        const pageType = (document.querySelector('meta[name~="pageType"]')?.getAttribute('content'))?.toString();
        return (pageType === 'splash');
    }

    return false;
}

/**
 * @description Get the url slug to be sent via Analytics
 * @returns Returns the URL slug
 */
export function getSlug(): string {
    let { pathname } = window.location;

    if (pathname.endsWith('/')) {
        pathname = pathname.slice(0, -1);
    }

    const matches = pathname.match(/welcome\/(.*)/);
    let slug : string = pathname.split('/').pop() || '';

    if (isV2Splash()) {
        slug = (isValidLocaleFormat(pathname.substr(1))) ? 'index' : slug;
    } else if (slug === 'welcome') {
        slug = 'welcome__mlp';
    } else if (matches && matches[1]) {
        slug = matches[1];
    } else if (isOlp() || !pathname) {
        slug = 'welcome';
    }

    return slug;
}

/**
 * @description Checks a HTMLElement properties to determine if the user can actually see the element
 * @param elem HTMLElement to check to see if it is visible to the user
 * @returns True = User can see it
 */
export function isVisible(elem: HTMLElement): boolean {
    const styles = window.getComputedStyle(elem);
    const opacity = parseInt(styles.getPropertyValue('opacity'), 10);

    return !!((elem.offsetWidth || elem.offsetHeight || elem.getClientRects().length) && opacity > 0);
}

/**
 * @description Returns whether the urlLang is a valid locale format
 * @param {String} urlLang - language from URL
 * @returns {Boolean} True if contextUrlPrefix is a valid config combination
 */
export function isValidLocaleFormat(urlLang) {
    return /^[a-zA-Z]{2}(-[a-zA-Z0-9]{2,3})?$/.test(urlLang);
}

/**
 * @description Get the url slug without locale
 * @returns Returns the URL slug
 */
export function getSlugWithoutLocale(): string {
    const { pathname } = window.location;
    const slugArray = pathname.split('/');

    if (isValidLocaleFormat(slugArray[1])) slugArray.splice(1, 1);

    const slug = slugArray.join('/');

    return slug;
}

/**
 * @description Strips any trailing slashes from a url
 * @param {String} url Url to be stripped of extra trailing slashes
 * @returns clean url
 */
export function stripEndSlashes(url: string): string {
    let cleanUrl = url;

    if (url.startsWith('/')) {
        cleanUrl = url.slice(1);
    }

    if (url.endsWith('/')) {
        return cleanUrl.replace(/\/+$/, '');
    }

    return cleanUrl;
}

/**
 * @description Converts the url string into an array of parts along slashes (i.e. /en-us/welcome to ['en-us', 'welcome'])
 * @param {String} url Url to be split
 * @returns {Array} url array
 */
export function convertUrlToArray(url: string): Array<string> {
    // Remove forward & trailing slash from pathname
    const path = stripEndSlashes(url);

    // This can produce array with empty string
    let parts = path.split('/');
    if (parts[0] === '') {
        parts = [];
    }

    return parts;
}
